import { useTranslation } from 'react-i18next';
import { Box, Button, Card, Dialog, DialogContent, DialogTitle, Grid, List, ListItemText } from '@mui/material';
import './NodesConfiguration.scss';
import { useParams } from 'react-router-dom';
import Debug from 'debug';
import axios from 'axios';
import { useEffect, useState } from 'react';
import i18next from 'i18next';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { AuthHeader } from '../../../utils/helpers/auth-headers';
import { AlertSeverity } from '../../../utils/enum/Alerts';
import { setAlertData } from '../../../stores/alertStore';
import { ManagementNode } from '../../../models/ManagementNode';
import { Alarm } from '../../../models/Alarm';

const debug = Debug('monitoring:workers');

export default function ManagementNodes() {
  const { t } = useTranslation();
  const i18nLang = i18next.language;
  const paramsRoute = useParams();
  const dispatch = useDispatch();
  const [open, setOpen] = useState<boolean>(false);
  const [alarms, setAlarms] = useState<Alarm[]>([]);
  const [managementNodes, setManagementNodes] = useState<ManagementNode[]>([]);
  const [selectedManagementNode, setSelectedManagementNode] = useState<ManagementNode>({} as ManagementNode);

  const StyledListItemText = styled(ListItemText)({
    '& .MuiTypography-root': {
      fontSize: 12,
    }
  });

  const getPexipManagement = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/mgtNodes`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        debug('pexip-management : ', response.data);
        setManagementNodes(response.data.data);
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      });
  };

  const getAlarms = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/alarms`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => setAlarms(response.data.data))
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
        }
        console.error(error);
      });
  };

  const hasAlarms = (managementNode: ManagementNode) => {
    for (const alarm of alarms) {
      if (alarm.node === managementNode.address) {
        return true;
      }
    }
    return false;
  };

  const getColorAlert = (managementNode: ManagementNode) => {
    for (const alarm of alarms) {
      if (alarm.node === managementNode.address && alarm.level === 'error') {
        return 'is-invalid';
      }
    }
    return 'is-warning';
  };

  useEffect(() => {
    getPexipManagement();
    getAlarms();
  }, []);

  return (
    <>
      <div className="category-container">
        {managementNodes.map((managementNode: ManagementNode) => (
          <Card key={managementNode.id} className="pointer" aria-hidden="true" onClick={() => { setSelectedManagementNode(managementNode); setOpen(true); }}>
            <div className="title-node">
              <strong>
                {t('InstanceConfiguration.managementNode.title')}: {managementNode.name} {hasAlarms(managementNode) && <FontAwesomeIcon className={getColorAlert(managementNode)} icon={faWarning} />}
              </strong>
            </div>
            <div className="pexip-management">
              <Grid container columns={12} spacing={1}>
                <Grid item xs={3}>
                  <div><strong>{ t('InstanceConfiguration.managementNode.version') }: </strong>{managementNode.version}</div>
                  <div><strong>{ t('InstanceConfiguration.managementNode.primary') }: </strong>{managementNode.primary ? <span className="is-true">{ t('InstanceConfiguration.managementNode.yes') }</span> : <span className="is-false">{ t('InstanceConfiguration.managementNode.no') }</span> }</div>
                </Grid>
                <Grid item xs={3}>
                  <div><strong>{ t('InstanceConfiguration.managementNode.address') }: </strong>{managementNode.address}</div>
                  <div><strong>{ t('InstanceConfiguration.managementNode.lastAttemptedContact') }:&nbsp;</strong>{new Date(managementNode.lastAttemptedContact).toLocaleString(i18nLang)}</div>
                </Grid>
                <Grid item xs={3}>
                  <div><strong>{ t('InstanceConfiguration.managementNode.lastReported') }: </strong>{new Date(managementNode.lastReported).toLocaleString(i18nLang)}</div>
                  <div><strong>{ t('InstanceConfiguration.managementNode.lastUpdated') }: </strong>{new Date(managementNode.lastUpdated).toLocaleString(i18nLang)}</div>
                </Grid>
                <Grid item xs={3}>
                  <div><strong>{ t('InstanceConfiguration.managementNode.resourceUri') }: </strong>{managementNode.resourceUri}</div>
                  <div><strong>{ t('InstanceConfiguration.managementNode.syncStatus') }: </strong>
                    {managementNode.syncStatus === 'SYNCED' ? (<span className="is-valid">{managementNode.syncStatus}</span>) : (<span className="is-invalid">{managementNode.syncStatus}</span>)}
                  </div>
                </Grid>
              </Grid>
            </div>
          </Card>
        ))}
      </div>

      <Dialog maxWidth={false} open={open} onClose={() => setOpen(false)}>
        {selectedManagementNode && (
          <Box>
            <DialogTitle>{selectedManagementNode.name}</DialogTitle>
            <DialogContent>
              { alarms?.length > 0 && (
                <div>
                  <div><strong>{t('InstanceConfiguration.worker.alarms')} :</strong></div>
                  { alarms.map((alarm: any, i: number) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i}>
                      { alarm?.node === selectedManagementNode.address && (
                        <Grid container spacing={2} columns={12}>
                          <Grid item xs={6}>
                            <List>
                              <StyledListItemText>
                                <strong>{t('InstanceAlarms.table.name')} : </strong>
                                <span className={alarm.level === 'warning' ? 'is-warning' : 'is-invalid'}>{alarm.name}</span>
                              </StyledListItemText>
                              <StyledListItemText>
                                <strong>{t('InstanceAlarms.table.details')} : </strong>
                                <span className={alarm.level === 'warning' ? 'is-warning' : 'is-invalid'}>{alarm.details}</span>
                              </StyledListItemText>
                              <StyledListItemText>
                                <strong>{t('InstanceAlarms.table.instance')} : </strong>
                                {alarm.instance}
                              </StyledListItemText>
                            </List>  
                          </Grid>
                          <Grid item xs={6}>
                            <List>
                              <StyledListItemText>
                                <strong>{t('InstanceAlarms.table.timeRaised')} : </strong>
                                {new Date(alarm.timeRaised).toLocaleString(i18nLang)}
                              </StyledListItemText>
                              <StyledListItemText>
                                <strong>{t('InstanceAlarms.table.identifier')} : </strong>
                                {alarm.identifier}
                              </StyledListItemText>
                              <StyledListItemText>
                                <strong>{t('InstanceAlarms.table.resourceUri')} : </strong>
                                {alarm.resourceUri}
                              </StyledListItemText>
                            </List>  
                          </Grid>
                        </Grid>
                      )}
                    </div>
                  ))}
                  <hr />
                </div> 
              )}
              <Grid container spacing={2} columns={12}>
                <Grid item xs={6}>
                  <List>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.address')}: </strong>
                      {selectedManagementNode.address}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.alternativeFqdn')}: </strong>
                      {selectedManagementNode.alternativeFqdn}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.configurationId')}: </strong>
                      {selectedManagementNode.configurationId}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.description')}: </strong>
                      {selectedManagementNode.description}
                    </StyledListItemText>
                    {/* <StyledListItemText>
                          <strong>{t('InstanceConfiguration.managementNode.dnsServers')}: </strong>
                          {selectedManagementNode.dnsServers}
                        </StyledListItemText> */}
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.enableSsh')}: </strong>
                      {selectedManagementNode.enableSsh}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.eventSinks')}: </strong>
                      {selectedManagementNode.eventSinks}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.gateway')}: </strong>
                      {selectedManagementNode.gateway}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.hostname')}: </strong>
                      {selectedManagementNode.hostname}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.httpProxy')}: </strong>
                      {selectedManagementNode.httpProxy}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.id')}: </strong>
                      {selectedManagementNode.id}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.initializing')}: </strong>
                      {selectedManagementNode.initializing}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.ipv6Address')}: </strong>
                      {selectedManagementNode.ipv6Address}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.ipv6Gateway')}: </strong>
                      {selectedManagementNode.ipv6Gateway}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.lastAttemptedContact')}: </strong>
                      {new Date(selectedManagementNode.lastAttemptedContact).toLocaleString(i18nLang)}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.lastReported')}: </strong>
                      {new Date(selectedManagementNode.lastReported).toLocaleString(i18nLang)}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.lastUpdated')}: </strong>
                      {new Date(selectedManagementNode.lastUpdated).toLocaleString(i18nLang)}
                    </StyledListItemText>
                  </List>
                </Grid>
                <Grid item xs={6}>
                  <List>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.mtu')}: </strong>
                      {selectedManagementNode.mtu}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.netmask')}: </strong>
                      {selectedManagementNode.netmask}
                    </StyledListItemText>
                    {/* <StyledListItemText>
                          <strong>{t('InstanceConfiguration.managementNode.ntpServers')}: </strong>
                          {selectedManagementNode.ntpServers}
                        </StyledListItemText> */}
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.primary')}: </strong>
                      {selectedManagementNode.primary ? (<span>{t('InstanceConfiguration.managementNode.yes')}</span>) : (<span>{t('InstanceConfiguration.managementNode.no')}</span>)}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.resourceUri')}: </strong>
                      {selectedManagementNode.resourceUri}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.snmpCommunity')}: </strong>
                      {selectedManagementNode.snmpCommunity}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.snmpMode')}: </strong>
                      {selectedManagementNode.snmpMode}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.snmpNetworkManagementSystem')}: </strong>
                      {selectedManagementNode.snmpNetworkManagementSystem}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.snmpSystemContact')}: </strong>
                      {selectedManagementNode.snmpSystemContact}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.snmpSystemLocation')}: </strong>
                      {selectedManagementNode.snmpSystemLocation}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.staticNatAddress')}: </strong>
                      {selectedManagementNode.staticNatAddress}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.staticRoutes')}: </strong>
                      {selectedManagementNode.staticRoutes}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.syncStatus')}: </strong>
                      {selectedManagementNode.syncStatus === 'SYNCED' ? (<span className="is-valid">{selectedManagementNode.syncStatus}</span>) : (<span className="is-invalid">{selectedManagementNode.syncStatus}</span>)}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.tlsCertificate')}: </strong>
                      {selectedManagementNode.tlsCertificate}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.upgradeStatus')}: </strong>
                      {selectedManagementNode.upgradeStatus}
                    </StyledListItemText>
                    <StyledListItemText>
                      <strong>{t('InstanceConfiguration.managementNode.version')}: </strong>
                      {selectedManagementNode.version}
                    </StyledListItemText>
                  </List>
                </Grid>
              </Grid>
              <Button variant="outlined" fullWidth onClick={() => { setOpen(false); }}>
                {t('RealTimeDetails.button.back')}
              </Button>
            </DialogContent>
          </Box>
        )};
      </Dialog>
    </>
  );
}
